/**
 * Phase 86AY.3 — Standalone /reset-password page.
 *
 * The invitation email links recipients to /reset-password?email=... — until
 * now this route didn't exist (only an in-modal flow inside LoginHub), so the
 * link landed on a blank screen (bug reported by admin: "bu ne beyaz ekran?").
 *
 * Workflow:
 *   • Email is pre-filled from the URL query param (read-only).
 *   • User enters 6-digit code received by mail.
 *   • Picks + confirms new password against the same rule helper used elsewhere.
 *   • POST /api/auth/reset-password → redirect to /giris on success.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, CheckCircle2, X, Check, Leaf, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { checkPasswordRules } from '../lib/passwordPolicy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static — defined outside the component to avoid the "no-unstable-nested-
// components" lint rule and reuse the same React node identity on each render.
const PassRule = ({ ok, label }) => (
  <div className={`flex items-center gap-1.5 text-[11px] ${ok ? 'text-emerald-600' : 'text-rose-500'}`}>
    {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}<span>{label}</span>
  </div>
);

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [email, setEmail]         = useState('');
  const [code, setCode]           = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confirm, setConfirm]     = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [showPw2, setShowPw2]     = useState(false);
  const [passTouched, setPassTouched]       = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [done, setDone]           = useState(false);

  useEffect(() => {
    const qpEmail = searchParams.get('email') || '';
    if (qpEmail) setEmail(qpEmail);
  }, [searchParams]);

  const rules = checkPasswordRules(newPass);
  const allRulesOk = Object.values(rules).every(Boolean);
  const mismatch = !!(newPass && confirm && newPass !== confirm);

  const submit = async (e) => {
    e.preventDefault();
    setPassTouched(true); setConfirmTouched(true);
    setError('');
    if (!email)          return setError(tr ? 'E-posta gerekli.' : 'Email is required.');
    if (!/^\d{6}$/.test(code)) return setError(tr ? 'Kod 6 haneli olmalı.' : 'Code must be 6 digits.');
    if (!allRulesOk)     return setError(tr ? 'Şifre kuralları karşılanmıyor.' : 'Password does not meet rules.');
    if (mismatch)        return setError(tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email, code, new_password: newPass,
      });
      // Phase 86AY.5 — Auto-login the invitee right after they set their
      // password. Previously the page redirected to /giris which felt
      // jarring ("I just typed everything — why am I being asked to log in
      // again?"). We re-authenticate behind the scenes and route to the
      // correct dashboard based on role + tenant_slug returned by login.
      try {
        const loginRes = await axios.post(`${API}/auth/login`, {
          email,
          password: newPass,
        });
        const data = loginRes.data || {};
        if (data.access_token) {
          // Mirror LoginHub.saveAndRedirect for customer admin (Green Zone
          // invitees only land here for customer-admin role). We use the
          // generic `cop31_token` / `cop31_user` keys read across the app.
          localStorage.setItem('cop31_token', data.access_token);
          localStorage.setItem('cop31_user',  JSON.stringify(data));
          localStorage.setItem('cop31_login_time', String(Date.now()));
          // Legacy / fallback keys still read by a few older pages.
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user',  JSON.stringify(data));
          setDone(true);
          const slug = data.tenant_slug || 'default';
          const target =
            data.role === 'ultra_admin'    ? '/ultra-admin/dashboard' :
            data.role === 'supplier_admin' ? `/tedarikci-admin/${slug}/dashboard` :
            data.role === 'contractor'     ? `/yuklenci-admin/${slug}/dashboard` :
                                             `/musteri-admin/${slug}/dashboard`;
          setTimeout(() => navigate(target, { replace: true }), 1200);
          return;
        }
      } catch (loginErr) {
        // Auto-login failed unexpectedly — surface a friendly fallback to the
        // login page rather than leaving the user stuck on a "done" screen.
        console.warn('reset-password auto-login failed:', loginErr);
      }
      setDone(true);
      setTimeout(() => navigate('/giris'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
    }}>
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="reset-password-card">
        <div className="bg-emerald-600 px-8 py-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/15 mb-3">
            <Leaf className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-white text-xl font-bold">
            {tr ? 'Şifrenizi Belirleyin' : 'Set Your Password'}
          </h1>
          <p className="text-white/80 text-sm mt-1">
            {tr ? 'COP 31 Antalya · Hesap Aktivasyon' : 'COP 31 Antalya · Account Activation'}
          </p>
        </div>

        {done ? (
          <div className="px-8 py-10 text-center" data-testid="reset-done">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-bold text-gray-800 text-lg mb-2">
              {tr ? 'Şifreniz Belirlendi · Giriş yapılıyor…' : 'Password Set · Signing you in…'}
            </h2>
            <p className="text-sm text-gray-500">
              {tr ? 'Stand panelinize yönlendiriliyorsunuz…' : 'Redirecting to your dashboard…'}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-8 py-6 space-y-3">
            <div>
              <Label className="text-xs font-semibold text-gray-600">{tr ? 'E-posta' : 'Email'}</Label>
              <Input
                type="email"
                value={email}
                readOnly
                data-testid="reset-email"
                className="mt-1 bg-gray-50 text-gray-700 cursor-not-allowed"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">
                {tr ? 'Doğrulama Kodu (Mailinizdeki 6 haneli kod)' : 'Verification Code (6-digit from your mail)'}
              </Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoFocus
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="• • • • • •"
                data-testid="reset-code"
                className="mt-1 text-center text-lg tracking-widest font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">{tr ? 'Yeni Şifre' : 'New Password'}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={newPass}
                  onChange={e => setNewPass(e.target.value)}
                  onBlur={() => setPassTouched(true)}
                  data-testid="reset-new-password"
                  className={`pr-9 ${passTouched && !allRulesOk ? 'border-rose-300 focus-visible:ring-rose-300' : ''}`}
                />
                <button type="button" onClick={() => setShowPw(s => !s)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-600">{tr ? 'Şifre (Tekrar)' : 'Confirm Password'}</Label>
              <div className="relative mt-1">
                <Input
                  type={showPw2 ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  onBlur={() => setConfirmTouched(true)}
                  data-testid="reset-confirm-password"
                  className={`pr-9 ${confirmTouched && mismatch ? 'border-rose-300 focus-visible:ring-rose-300' : ''}`}
                />
                <button type="button" onClick={() => setShowPw2(s => !s)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                  {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmTouched && mismatch && (
                <p className="text-[11px] text-rose-600 mt-1" data-testid="reset-mismatch">
                  {tr ? 'Şifreler eşleşmiyor.' : 'Passwords do not match.'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 bg-gray-50 border border-gray-100 rounded-lg p-3">
              <PassRule ok={rules.length}    label={tr ? 'En az 8 karakter' : 'Min 8 chars'} />
              <PassRule ok={rules.uppercase} label={tr ? '1 büyük harf'     : '1 uppercase'} />
              <PassRule ok={rules.lowercase} label={tr ? '1 küçük harf'     : '1 lowercase'} />
              <PassRule ok={rules.number}    label={tr ? '1 rakam'          : '1 digit'} />
              <PassRule ok={rules.special}   label={tr ? '1 özel karakter'  : '1 special'} />
            </div>

            {error && (
              <p className="text-xs text-rose-600 mt-1" data-testid="reset-error">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl">
              {loading ? (tr ? 'Kaydediliyor…' : 'Saving…') : (tr ? 'Şifremi Kaydet' : 'Save Password')}
            </Button>

            <button
              type="button"
              onClick={() => navigate('/giris')}
              className="w-full inline-flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 pt-1">
              <ArrowLeft className="w-3 h-3" />
              {tr ? 'Giriş Sayfasına Dön' : 'Back to Login'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
